/**
 * QuickFilters — one-click chip filters for the inbox list.
 *
 * Plus saved-searches: persists in localStorage. Click chip to apply, X to remove.
 *
 * Iteration 104 — Batch 3 (g + h).
 */
import React, { useEffect, useState } from 'react';
import { Bookmark, X, Star, Paperclip, Mail, Calendar, Users } from 'lucide-react';

const SAVED_KEY = 'intalk:email:savedSearches';

const QUICK_OPTIONS = [
  { id: 'unread',      label: 'Unread',         icon: Mail },
  { id: 'starred',     label: 'Starred',        icon: Star },
  { id: 'attachments', label: 'Has attachment', icon: Paperclip },
  { id: 'leads',       label: 'From leads',     icon: Users },
  { id: 'today',       label: 'Today',          icon: Calendar },
];

// Apply a quick filter to a list of emails (client-side).
export function applyQuickFilter(emails, quickFilter) {
  if (!quickFilter || quickFilter === 'all') return emails;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ts = (e) => e.received_at || e.sent_at || e.synced_at || e.created_at;
  switch (quickFilter) {
    case 'unread':      return emails.filter((e) => !e.is_read);
    case 'starred':     return emails.filter((e) => e.starred);
    case 'attachments': return emails.filter((e) => e.has_attachments);
    case 'leads':       return emails.filter((e) => !!e.lead_id);
    case 'today':       return emails.filter((e) => {
      const d = new Date(ts(e));
      return !isNaN(d.getTime()) && d >= today;
    });
    default: return emails;
  }
}

export function loadSavedSearches() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function persistSavedSearches(arr) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
}

export default function QuickFilters({
  quickFilter,
  setQuickFilter,
  search,
  setSearch,
}) {
  const [saved, setSaved] = useState([]);

  useEffect(() => { setSaved(loadSavedSearches()); }, []);

  const saveCurrent = () => {
    const q = (search || '').trim();
    if (!q) return;
    const name = window.prompt('Name this saved search:', q.slice(0, 30));
    if (!name) return;
    if (saved.some((s) => s.name === name)) return;
    const next = [...saved, { id: `${Date.now()}`, name, query: q }];
    setSaved(next); persistSavedSearches(next);
  };

  const removeSaved = (id) => {
    const next = saved.filter((s) => s.id !== id);
    setSaved(next); persistSavedSearches(next);
  };

  return (
    <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/30 flex items-center gap-1.5 flex-wrap" data-testid="quick-filters">
      {/* All chip */}
      <button
        onClick={() => setQuickFilter('all')}
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition ${
          !quickFilter || quickFilter === 'all'
            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
            : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
        }`}
        data-testid="quick-filter-all"
      >
        All
      </button>

      {QUICK_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = quickFilter === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => setQuickFilter(active ? 'all' : opt.id)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition ${
              active
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
            }`}
            data-testid={`quick-filter-${opt.id}`}
          >
            <Icon className="w-3 h-3" />
            {opt.label}
          </button>
        );
      })}

      {/* Saved searches */}
      {saved.length > 0 && (
        <>
          <span className="mx-1 text-slate-300 dark:text-slate-600">|</span>
          {saved.map((s) => {
            const active = search === s.query;
            return (
              <span
                key={s.id}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition border ${
                  active
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600'
                }`}
                data-testid={`saved-search-${s.id}`}
              >
                <button onClick={() => setSearch(active ? '' : s.query)} className="inline-flex items-center gap-1">
                  <Bookmark className="w-3 h-3" />
                  <span className="truncate max-w-[120px]">{s.name}</span>
                </button>
                <button
                  onClick={() => removeSaved(s.id)}
                  className="ml-0.5 hover:text-rose-500"
                  title="Remove saved search"
                  data-testid={`remove-saved-${s.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </>
      )}

      {/* Save current search button */}
      {search && search.trim() && !saved.some((s) => s.query === search.trim()) && (
        <button
          onClick={saveCurrent}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-dashed border-slate-300 dark:border-slate-600"
          data-testid="save-search-btn"
          title="Save current search as a chip"
        >
          <Bookmark className="w-3 h-3" /> Save search
        </button>
      )}
    </div>
  );
}
